import { useState } from "react";
import Template from "../app/Template";
import Container from "../layout/Container";
import Card from "../layout/Card";
import SimpleButton from "./SimpleButton";
import TextInput from "../forms/TextInput";
import TextArea from "../forms/TextArea";
import { useNavigate } from "react-router-dom";
import AlertModal from "./AlertModal";
import ProcessingModal from "./ProcessingModal";
import ManualCsvUpload from "./csv/ManualCsvUpload";
import { extractApiError } from "../../utils/validation";
import { isValidYoutubeUrl } from "../../utils/youtube";

/**
 * Validating the YouTube URL here as well as server-side means a typo never leaves
 * the browser: the form used to POST anything typed, so "nourl" cost a round-trip,
 * and a well-formed-but-wrong id cost a YouTube Data API call server-side.
 */
export default function IngestionForm() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"youtube" | "manual">("manual");
  /**
   * How manual content is entered. Kept separate from `mode` on purpose: the top row
   * chooses where content comes from, this chooses how it is typed in. A CSV carries
   * exactly the fields of the manual form, so it belongs inside Manual Entry rather
   * than beside YouTube as a third source.
   */
  const [manualMode, setManualMode] = useState<"single" | "csv">("single");
  const [loading, setLoading] = useState(false);
  const [alertModal, setAlertModal] = useState({ show: false, title: "", message: "", type: "info" as "success" | "error" | "warning" | "info" });
  const [ingestionAbortController, setIngestionAbortController] = useState<AbortController | null>(null);

  // YouTube fields
  const [youtubeUrl, setYoutubeUrl] = useState("");
  /** Optional: min 50 chars to skip captions / Whisper */
  const [youtubeTranscript, setYoutubeTranscript] = useState("");

  // Only complain once something has been typed, so the field is not red on load.
  const youtubeUrlInvalid = youtubeUrl.trim().length > 0 && !isValidYoutubeUrl(youtubeUrl);

  // Manual fields
  const [contentType, setContentType] = useState("blog");
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [text, setText] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");

  const handleYoutubeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isValidYoutubeUrl(youtubeUrl)) {
      setAlertModal({
        show: true,
        title: "Invalid YouTube URL",
        message:
          "Enter a full YouTube video URL, for example https://www.youtube.com/watch?v=dQw4w9WgXcQ",
        type: "warning",
      });
      return;
    }

    const abortController = new AbortController();
    setIngestionAbortController(abortController);
    setLoading(true);

    try {
      const token = localStorage.getItem('access');
      const response = await fetch("/api/pipeline/ingestion/youtube/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { "Authorization": `Bearer ${token}` }),
        },
        body: JSON.stringify({
          url: youtubeUrl,
          ...(youtubeTranscript.trim().length >= 50
            ? { transcript: youtubeTranscript.trim() }
            : {}),
        }),
        signal: abortController.signal,
      });

      const data = await response.json();

      if (response.ok) {
        setLoading(false);
        // 202: the video's title, channel and image were stored but YouTube would not
        // serve its captions, so the item is waiting for a pasted transcript. Reporting
        // that as an unqualified success would send the operator to a dashboard row
        // that never processes, with nothing saying why.
        const pending = data.reason === "transcript_pending";
        setAlertModal({
          show: true,
          title: pending ? "Transcript Needed" : "Content Ingested",
          message: pending
            ? data.message
            : `YouTube content ingested successfully! ID: ${data.content_id}`,
          type: pending ? "warning" : "success"
        });
        if (!pending) setTimeout(() => navigate("/pipeline/dashboard"), 1500);
      } else {
        setLoading(false);
        setAlertModal({
          show: true,
          title: "Ingestion Failed",
          message: extractApiError(data, "Failed to ingest content. Please check the URL and try again."),
          type: "error"
        });
      }
    } catch (error: any) {
      setLoading(false);
      if (error.name === 'AbortError') {
        setAlertModal({
          show: true,
          title: "Ingestion Cancelled",
          message: "YouTube ingestion was cancelled by user.",
          type: "warning"
        });
      } else {
        console.error("Error ingesting YouTube content:", error);
        setAlertModal({
          show: true,
          title: "Network Error",
          message: "Unable to connect to the server. Please check your connection.",
          type: "error"
        });
      }
    }
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (text.length < 50) {
      setAlertModal({
        show: true,
        title: "Validation Error",
        message: "Text content must be at least 50 characters long.",
        type: "warning"
      });
      return;
    }

    const abortController = new AbortController();
    setIngestionAbortController(abortController);
    setLoading(true);

    try {
      const token = localStorage.getItem('access');
      const response = await fetch("/api/pipeline/ingestion/manual/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { "Authorization": `Bearer ${token}` }),
        },
        body: JSON.stringify({
          content_type: contentType,
          title,
          author,
          text,
          source_url: sourceUrl || undefined,
        }),
        signal: abortController.signal,
      });

      const data = await response.json();

      if (response.ok) {
        setLoading(false);
        setAlertModal({
          show: true,
          title: "Content Ingested",
          message: `Manual content ingested successfully! ID: ${data.content_id}`,
          type: "success"
        });
        setTimeout(() => navigate("/pipeline/dashboard"), 1500);
      } else {
        setLoading(false);
        setAlertModal({
          show: true,
          title: "Ingestion Failed",
          message: extractApiError(data, "Failed to ingest content. Please check all fields and try again."),
          type: "error"
        });
      }
    } catch (error: any) {
      setLoading(false);
      if (error.name === 'AbortError') {
        setAlertModal({
          show: true,
          title: "Ingestion Cancelled",
          message: "Manual ingestion was cancelled by user.",
          type: "warning"
        });
      } else {
        console.error("Error ingesting manual content:", error);
        setAlertModal({
          show: true,
          title: "Network Error",
          message: "Unable to connect to the server. Please check your connection.",
          type: "error"
        });
      }
    }
  };

  const handleCancelIngestion = () => {
    if (ingestionAbortController) {
      ingestionAbortController.abort();
      setLoading(false);
    }
  };

  return (
    <Template navbarMode="solid">
      <Container className="mt-20 sm:mt-24 md:mt-32 mb-12 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <Card colour="yellow" className="mb-6" paddingX="px-6 sm:px-8" paddingY="py-4 sm:py-5">
          <h1 className="text-dark-grey text-2xl sm:text-3xl md:text-4xl font-bold mb-1">
            Ingest Content
          </h1>
          <p className="text-dark-grey text-sm sm:text-base">
            Add new content to the processing pipeline
          </p>
        </Card>

        {/* Mode Selector */}
        <div className="mb-5 flex flex-wrap gap-2 sm:gap-3">
          <button
            onClick={() => setMode("manual")}
            className={`px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-sm sm:text-base font-semibold transition shadow-sm ${
              mode === "manual"
                ? "bg-orange text-white border-2 border-orange shadow-md"
                : "bg-white text-dark-grey border-2 border-gray-300 hover:bg-gray-50 hover:border-dark-grey"
            }`}
          >
            Manual Entry
          </button>
          <button
            onClick={() => setMode("youtube")}
            className={`px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-sm sm:text-base font-semibold transition shadow-sm ${
              mode === "youtube"
                ? "bg-orange text-white border-2 border-orange shadow-md"
                : "bg-white text-dark-grey border-2 border-gray-300 hover:bg-gray-50 hover:border-dark-grey"
            }`}
          >
            YouTube URL
          </button>
        </div>

        {/* Forms */}
        {mode === "youtube" ? (
          <Card colour="white" paddingX="px-6 sm:px-8" paddingY="py-5 sm:py-6">
            <form onSubmit={handleYoutubeSubmit} className="space-y-4">
              <div>
                <label className="block text-dark-grey font-semibold mb-2 text-sm sm:text-base">
                  YouTube Video URL
                </label>
                <TextInput
                  id="youtube-url"
                  type="url"
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                  required
                />
                {youtubeUrlInvalid ? (
                  <p className="text-xs sm:text-sm text-red-600 mt-1.5">
                    That does not look like a YouTube video URL. Expected something like
                    https://www.youtube.com/watch?v=dQw4w9WgXcQ
                  </p>
                ) : (
                  <p className="text-xs sm:text-sm text-gray-500 mt-1.5">
                    Enter the full YouTube video URL. The system fetches metadata and transcript
                    from captions (or Whisper if configured), unless you paste a transcript below.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-dark-grey font-semibold mb-2 text-sm sm:text-base">
                  Transcript (optional)
                </label>
                <TextArea
                  id="youtube-transcript"
                  value={youtubeTranscript}
                  onChange={(e) => setYoutubeTranscript(e.target.value)}
                  placeholder="Paste an existing transcript (min 50 characters) to skip caption download…"
                  rows={6}
                  required={false}
                />
                <p className="text-xs sm:text-sm text-gray-500 mt-1.5">
                  {youtubeTranscript.trim().length > 0 && youtubeTranscript.trim().length < 50
                    ? `${youtubeTranscript.trim().length}/50 minimum to use pasted transcript`
                    : youtubeTranscript.trim().length >= 50
                      ? "Pasted transcript will be used instead of YouTube captions."
                      : "Leave empty to fetch captions automatically."}
                </p>
              </div>

              <div className="flex flex-wrap gap-2 sm:gap-3 pt-3">
                <SimpleButton
                  text={loading ? "Ingesting..." : "Ingest Video"}
                  colour="orange"
                  type="submit"
                  disabled={loading || !youtubeUrl || youtubeUrlInvalid}
                />
                <SimpleButton
                  text="Cancel"
                  colour="yellow"
                  onClick={() => navigate("/pipeline/dashboard")}
                />
              </div>
            </form>
          </Card>
        ) : (
          <Card colour="white" paddingX="px-6 sm:px-8" paddingY="py-5 sm:py-6">
            {/* Entry method — a segmented control, not another pill row, so the two
                levels of choice stay visually distinct. */}
            <div
              role="tablist"
              aria-label="Manual entry method"
              className="mb-5 inline-flex rounded-full border-2 border-gray-300 p-1 bg-gray-50"
            >
              {([
                { key: "single", label: "Single entry" },
                { key: "csv", label: "CSV upload" },
              ] as const).map(({ key, label }) => (
                <button
                  key={key}
                  role="tab"
                  type="button"
                  aria-selected={manualMode === key}
                  onClick={() => setManualMode(key)}
                  className={`px-4 sm:px-5 py-1.5 rounded-full text-sm sm:text-base font-semibold transition ${
                    manualMode === key
                      ? "bg-white text-dark-grey shadow-sm"
                      : "text-gray-500 hover:text-dark-grey"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {manualMode === "csv" ? (
              <ManualCsvUpload
                onAlert={(alert) => setAlertModal({ show: true, ...alert })}
              />
            ) : (
            <form onSubmit={handleManualSubmit} className="space-y-4">
              <div>
                <label className="block text-dark-grey font-semibold mb-2 text-sm sm:text-base">
                  Content Type *
                </label>
                <select
                  value={contentType}
                  onChange={(e) => setContentType(e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange focus:border-orange text-sm sm:text-base bg-white transition"
                  required
                >
                  <option value="blog">Blog Post</option>
                  <option value="article">Article</option>
                  <option value="book">Book</option>
                  <option value="post">Social Media Post</option>
                  <option value="podcast">Podcast Transcript</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-dark-grey font-semibold mb-2 text-sm sm:text-base">
                    Title *
                  </label>
                  <TextInput
                    id="content-title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter content title"
                    required
                  />
                </div>

                <div>
                  <label className="block text-dark-grey font-semibold mb-2 text-sm sm:text-base">
                    Author *
                  </label>
                  <TextInput
                    id="content-author"
                    type="text"
                    value={author}
                    onChange={(e) => setAuthor(e.target.value)}
                    placeholder="Author name"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-dark-grey font-semibold mb-2 text-sm sm:text-base">
                  Source URL (Optional)
                </label>
                <TextInput
                  id="source-url"
                  type="url"
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  placeholder="https://example.com/article"
                  required={false}
                />
              </div>

              <div>
                <label className="block text-dark-grey font-semibold mb-2 text-sm sm:text-base">
                  Full Text Content *
                </label>
                <TextArea
                  id="content-text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Paste the full text content here (minimum 50 characters)..."
                  rows={10}
                  required
                />
                <div className="flex items-center justify-between mt-1.5">
                  <p className="text-xs sm:text-sm text-gray-500">
                    {text.length} / 50 minimum characters
                  </p>
                  {text.length >= 50 && (
                    <span className="text-xs sm:text-sm text-green-600 font-medium">
                      ✓ Minimum met
                    </span>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 sm:gap-3 pt-3">
                <SimpleButton
                  text={loading ? "Ingesting..." : "Ingest Content"}
                  colour="orange"
                  type="submit"
                  disabled={loading || !title || !author || text.length < 50}
                />
                <SimpleButton
                  text="Cancel"
                  colour="yellow"
                  onClick={() => navigate("/pipeline/dashboard")}
                />
              </div>
            </form>
            )}
          </Card>
        )}
      </Container>

      {/* Modals */}
      <AlertModal
        show={alertModal.show}
        setShow={(show) => setAlertModal({ ...alertModal, show })}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />

      <ProcessingModal
        show={loading}
        title={mode === "youtube" ? "Fetching YouTube Content" : "Ingesting Content"}
        message={
          mode === "youtube"
            ? "Extracting transcript and metadata from YouTube. This may take a moment..."
            : "Saving your content to the pipeline. Please wait..."
        }
        onCancel={handleCancelIngestion}
      />
    </Template>
  );
}
